// src/components/billing/PlanSelector.tsx
/**
 * Plan selection component for displaying subscription tiers.
 * Shows tier comparison with upgrade/downgrade actions.
 */

import React from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  alpha,
  CircularProgress,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import { SubscriptionTier } from "../../hooks/useBilling";
import { useTranslation } from "../../i18n";
import { glass } from "../../theme/glassTokens";

interface PlanSelectorProps {
  tiers: SubscriptionTier[];
  currentTier?: string;
  onSelectTier: (tierName: string) => void;
  loading?: boolean;
}

const PlanSelector: React.FC<PlanSelectorProps> = ({
  tiers,
  currentTier,
  onSelectTier,
  loading = false,
}) => {
  const theme = useTheme();
  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();

  // Filter out free tier from selection (it's the default/fallback)
  const selectableTiers = tiers.filter((t) => t.name !== "free" && t.name !== "enterprise");
  
  // "professional" DB key = AI Professional display tier — recommended
  const isRecommended = (tierName: string) => tierName === "professional";

  // Feature type for the list
  interface Feature {
    text: string;
    included: boolean;
  }

  // Get feature list for a tier
  const getFeatures = (tier: SubscriptionTier): Feature[] => {
    const features: Feature[] = [];
    
    // Patient limit
    if (tier.max_patients === 0) {
      features.push({ text: "Unlimited patients", included: true });
    } else {
      features.push({ text: `${tier.max_patients} patients`, included: true });
    }
    
    // Documents
    if (tier.max_documents_per_month > 0) {
      features.push({ text: `${tier.max_documents_per_month} documents/month`, included: true });
    }
    
    // AI extraction
    if (tier.can_use_ai_extraction) {
      features.push({ text: "AI extraction", included: true });
      if (tier.max_tokens_per_month > 0) {
        features.push({ 
          text: `${(tier.max_tokens_per_month / 1000).toFixed(0)}K tokens/month`, 
          included: true 
        });
      }
    } else {
      features.push({ text: "AI extraction", included: false });
    }

    // Natural-language analytics (NL query). Included on Professional and above
    // via has_analytics_filter, even when document AI extraction is not.
    if (tier.has_analytics_filter) {
      features.push({ text: "AI-powered natural-language analytics", included: true });
    } else {
      features.push({ text: "AI-powered natural-language analytics", included: false });
    }

    // Voice
    if (tier.can_use_voice) {
      features.push({ text: "Voice transcription", included: true });
      if (tier.max_voice_minutes_per_month > 0) {
        features.push({ 
          text: `${tier.max_voice_minutes_per_month} voice minutes/month`, 
          included: true 
        });
      }
    } else {
      features.push({ text: "Voice transcription", included: false });
    }
    
    // Users
    if (tier.max_users > 1) {
      features.push({ text: `${tier.max_users} team members`, included: true });
    }
    
    // Export
    if (tier.can_export_csv || tier.can_export_api) {
      features.push({ text: "Data export", included: true });
    } else {
      features.push({ text: "Data export", included: false });
    }
    
    // Cross-org sharing
    if (tier.can_cross_org_share) {
      features.push({ text: "Cross-organization sharing", included: true });
    }
    
    // API
    if (tier.can_use_api) {
      features.push({ text: "API access", included: true });
    }
    
    // Priority support
    if (tier.has_priority_support) {
      features.push({ text: "Priority support", included: true });
    }
    
    return features;
  };

  return (
    <Grid container spacing={3}>
      {selectableTiers.map((tier) => {
        const isCurrent = currentTier === tier.name;
        const recommended = isRecommended(tier.name);
        const features = getFeatures(tier);
        
        return (
          <Grid item xs={12} md={4} key={tier.name} sx={{ pt: recommended || isCurrent ? 2 : undefined }}>
            <Box
              sx={{
                height: "100%",
                position: "relative",
                overflow: "visible",
                p: { xs: 1.5, sm: 2.5 },
                borderRadius: 2,
                background: recommended ? glass.panelSunken : glass.panel,
                border: recommended
                  ? `2px solid ${alpha(glass.accent, 0.5)}`
                  : isCurrent
                  ? `2px solid ${alpha(theme.palette.success.light, 0.5)}`
                  : `1px solid ${glass.panelBorder}`,
                backdropFilter: "blur(8px)",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: recommended ? glass.selectedBorder : glass.panelBorder,
                  transform: "translateY(-2px)",
                  boxShadow: `0 4px 20px ${alpha(glass.accent, 0.1)}`,
                },
              }}
            >
              {recommended && (
                <Chip
                  icon={<StarIcon />}
                  label={t("billing.popular", "Popular")}
                  color="primary"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -12,
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                />
              )}
              
              {isCurrent && (
                <Chip
                  label={t("billing.current_plan", "Current Plan")}
                  color="success"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -12,
                    right: 16,
                  }}
                />
              )}
              
              <Box sx={{ pt: recommended ? 2 : 1 }}>
                <Typography variant="h5" component="h3" gutterBottom sx={{ color: textPrimary }}>
                  {tier.display_name}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="h3"
                    component="span"
                    sx={{ fontWeight: 700, color: textPrimary }}
                  >
                    {tier.price_monthly}
                  </Typography>
                  {tier.price_monthly_cents > 0 && (
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ ml: 1, color: textSecondary }}
                    >
                      /month
                    </Typography>
                  )}
                </Box>
                
                <Typography
                  variant="body2"
                  sx={{ mb: 3, minHeight: 40, color: textSecondary }}
                >
                  {tier.description}
                </Typography>
                
                <Button
                  variant={recommended ? "contained" : "outlined"}
                  color={isCurrent ? "success" : "primary"}
                  fullWidth
                  disabled={isCurrent || loading}
                  onClick={() => onSelectTier(tier.name)}
                  sx={{ mb: 3 }}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : isCurrent ? (
                    "Current Plan"
                  ) : (
                    "Select Plan"
                  )}
                </Button>
                
                <List dense disablePadding>
                  {features.map((feature, idx) => (
                    <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {feature.included ? (
                          <CheckIcon
                            fontSize="small"
                            sx={{ color: "success.main" }}
                          />
                        ) : (
                          <CloseIcon
                            fontSize="small"
                            sx={{ color: glass.textAbsent }}
                          />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.text}
                        primaryTypographyProps={{
                          variant: "body2",
                          color: feature.included
                            ? textPrimary
                            : glass.textMuted,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          </Grid>
        );
      })}
      
      {/* Enterprise tier */}
      <Grid item xs={12}>
        <Box
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            borderRadius: 2,
            background: glass.panel,
            border: `1px dashed ${glass.panelBorder}`,
            backdropFilter: "blur(8px)",
            transition: "border-color 0.2s ease",
            "&:hover": { borderColor: glass.headerDivider },
          }}
        >
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom sx={{ color: textPrimary }}>
                Enterprise
              </Typography>
              <Typography variant="body2" sx={{ color: textSecondary }}>
                For hospitals, health systems, large CROs, and pharmaceutical companies.
                Custom pricing with unlimited users, SSO, audit logs, and dedicated support.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { md: "right" } }}>
              <Button
                variant="outlined"
                color="primary"
                href="mailto:enterprise@staffbotics.com?subject=Enterprise%20Plan%20Inquiry"
              >
                Contact Sales
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
};

export default PlanSelector;
